import React, { useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid, Splat } from '@react-three/drei';
import * as THREE from 'three';
import './App.css';

import output from './data/output.json';
import outputjs from './data/output-js2.json';
import outputcolmap from './data/output-colmap.json';
import { OutputVisualizeOpenCV } from './components/OutputVisualizeOpenCV.tsx';
import { 
  OutputVisualizeColmap, Frame, ProcessFrames,
  computeFocalpoint, FrameColmap, findNearestFrame
} from './components/OutputVisualizeColmap.tsx';

function Box() {
  return (
    <mesh castShadow rotation={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="white" />
    </mesh>
  );
}

function App() {

  let [processedframes, setProcessedFrames] = React.useState<Frame[] | null>(null);

  const colmapframes = outputcolmap.frames as FrameColmap[];

  let [euler, setEuler] = React.useState<THREE.Euler>(new THREE.Euler(0, 0, 0));

  let [splatPosition, setSplatPosition] = React.useState<THREE.Vector3>(new THREE.Vector3(0, 0, 0));
  let [splatRotation, setSplatRotation] = React.useState<THREE.Quaternion>(new THREE.Quaternion(0, 0, 0, 1));

  useEffect(() => {

    let adjustpos = new THREE.Vector3();
    let adjustquat = new THREE.Quaternion();
    let frames = ProcessFrames(colmapframes, adjustpos, adjustquat);
    setProcessedFrames(frames);

    //const frame0 = frames![0];
    
    const center = computeFocalpoint(frames!);
    const rotationadjust = adjustquat.clone().conjugate();
  
    console.log("center", center);

    setSplatPosition(center.clone().negate());
    setSplatRotation(rotationadjust);

  }, []);

  //todo: add the viewer

  //todo: fixing the splat
  //     - I need the center for offset
  //     - I need frame0 rotation inverse for offset
  //     - I need to process colmap out here, pass the center, rotation into the component
  //     - I need to reprocess the data to json

  return (
    <div className="App" style={{ width: '500px', height: '400px' }}>
      <Canvas shadows camera={{ position: [3, 3, 3] }}>
        <ambientLight intensity={0.5} />
        <directionalLight 
          position={[5, 5, 5]}
          intensity={1}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />

        {/*   
        <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
          <planeGeometry args={[10, 10]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
       */}

        <Splat
          scale={1}
          position={splatPosition}
          quaternion={splatRotation}
          src="statue-splat.splat"
        />

         {/*
        <Box />
        
        
        <OutputVisualizeOpenCV frames={output.frames} />

        <OutputVisualizeOpenCV frames={outputjs.frames} offset={[0, 4, 0]} />
        */}
        <OutputVisualizeColmap frames={outputcolmap.frames} />
          {/** 
        <Grid
          args={[10, 10]}
          position={[0, -0.99, 0]}
          cellSize={1}
          cellThickness={1}
          cellColor="#ffffff"
          sectionSize={1}
        />
        <axesHelper />
        */}     
        <OrbitControls enableZoom={true} onChange={(e) => {
          let rotation = e!.target.object.quaternion;
          let euler = new THREE.Euler().setFromQuaternion(rotation, 'YXZ');
          setEuler(euler);

          }} />
      </Canvas>
      <div>
      {processedframes && <FrameViewer frames={processedframes} eulertarget={euler} />
      }
      </div>
    </div>
  );
};

interface ViewFrame {
  frame: Frame,
  image: HTMLImageElement,// this is returned by fetch
  imageURL?: string | null, // this is the objectURL of the image
  imageLoaded: boolean
}

interface TargetFrameData {
  center: { x: number; y: number };
  imageURL: string;
  image: HTMLImageElement;
}

function FrameViewer({ frames, eulertarget } : { frames: Frame[], eulertarget: THREE.Euler}) {

  const [viewframes, setViewFrames] = React.useState<ViewFrame[]>([]);
  
  const [targetframe, setTargetFrame] = React.useState<ViewFrame | null>(null);
  const [targetframeLoaded, setTargetFrameLoaded] = React.useState<boolean>(false);

  //const [imageURL, setImageURL] = React.useState<string>("");
  //const [center, setCenter] = React.useState<{x: number, y: number}>({x: 0, y: 0});

  const [targetFrameData, setTargetFrameData] = React.useState<TargetFrameData | null>(null);

  useEffect( () => {
    
    // convert all the frames to viewframes.
    // todo: sort the frames by angle distance (they're sorted on the test data)
    // start loading all the frames in an async function
    let viewframes = frames.map((frame) => {
      return ({
        frame: frame,
        image: new Image(),
        imageURL: null,
      } as ViewFrame)
    });

    setViewFrames(viewframes);

    async function loadFrames(frames: ViewFrame[]) {
      const THREADS = 4;
      const queue = [...frames];
      
      const worker = async (workerId: number) => {
        while (queue.length > 0) {
          const frame = queue.shift();
          if (!frame) break;
          
          const image = await fetch('statue/optimized/' + frame.frame.frame + ".webp");
          frame.imageURL = URL.createObjectURL(await image.blob());
          //fix frame.image cors
          frame.image.crossOrigin = "Anonymous";
          
          frame.image.src = frame.imageURL!;
          frame.image.onload = () => {
            frame.imageLoaded = true;
          };

          if(frame == targetframe && !targetframeLoaded) {
            setTargetFrameLoaded(true);
          }
          console.log(`Worker ${workerId} loaded frame ${frame.frame.frame}`);
        }
      };
    
      const workers = Array.from({ length: THREADS }, (_, i) => worker(i));
      await Promise.all(workers);
    };


    loadFrames(viewframes);


  }, []);

  useEffect(() => {

    // try to switch frames if the angle changes.
    // if not found then it needs to switch

    //handle case: image is still loading...
    //once the image is loaded, then switch frames.

    if(viewframes.length == 0) return;

    let newtarget = findNearestFrame(frames, eulertarget.y, eulertarget.x);

    const viewframe = viewframes.find((frame) => frame.frame.frame === newtarget.frame);

    if(viewframe)
    {
      setTargetFrame(viewframe);
      //setImageURL(viewframe.imageURL!);
     // setCenter(viewframe.frame.center2d!);

      setTargetFrameData({
        center: {x: viewframe.frame.center2d!.x, y: viewframe.frame.center2d!.y},
        imageURL: viewframe.imageURL!,
        image: viewframe.image!
      });

      console.log("Target Frame: " + targetframe?.frame.frame);
    }

  }, [eulertarget, frames]);

  /*
  useEffect(() => {
    if(targetframe && targetframe.imageURL != imageURL)
    {
      setImageURL(targetframe!.imageURL!);

      console.log("Target Frame: " + targetframe.frame.frame);
    }

  },[targetframe,targetframeLoaded]);
  */

  console.log("targetframe?.frame.center2d", targetframe?.frame.center2d);

  return (
    <>
    { targetFrameData && targetFrameData.imageURL && (
      <div>
      <CanvasImageWithCenter 
      image={targetFrameData.image} 
      center={targetFrameData.center} 
      dimensions={{x: 1920, y: 1080}} 
      frameHeight={targetframe?.frame.height!}
      rotation={targetframe?.frame.euler!.z! - targetframe?.frame.rollaverage!}/>
      
      {/*
      <ImageCropper imageUrl={targetFrameData.imageURL} center={targetFrameData.center} dimensions={{x: 1920, y: 1080}} />
      */}

      <br/>
      </div>
    )
    }

    {/*
    
    *<ScaledImageWithPivot imageSrc={imageURL} pivot={targetframe!.frame!.center2d!} scale={1} containerPivot={null}/>
    
    <div style={{ position: 'absolute', bottom: 0, left: 0, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {viewframes.map((viewframe, index) => (
        viewframe.imageURL && (
          <img 
            key={index}
            src={viewframe.imageURL} 
            alt={`Frame ${viewframe.frame.frame}`}
            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          />
        )
      ))}
    </div>
    
    */}
    </>
    
  );

}

const ScaledImageWithPivot = ({
  imageSrc,
  pivot,           // the point in the image (in px) that should remain fixed, e.g. { x: 500, y: 300 }
  scale,           // the scaling factor, e.g. 1.5
  containerPivot,  // optional: the point in the container where the pivot should align (in px). Default is container center.
}) => {
  const containerWidth = 1920;
  const containerHeight = 1080;
  
  // Default fixed point in container is its center
  const fixedPoint = containerPivot || { 
    x: containerWidth / 2, 
    y: containerHeight / 2 
  };

  // Compute the initial top/left so that the image's pivot aligns with the container’s fixed point.
  // Without scaling the pivot pixel (at pivot.x, pivot.y) would be at fixedPoint.
  const left = fixedPoint.x - pivot.x;
  const top = fixedPoint.y - pivot.y;

  return (
    <div
      style={{
        position: "relative",
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        overflow: "hidden",
        border: "1px solid #ccc", // just to visualize the container
      }}
    >
      <img
        src={imageSrc}
        alt="Scaled with custom pivot"
        style={{
          position: "absolute",
          left: `${left}px`,
          top: `${top}px`,
          // By setting transformOrigin to the pivot (in image–space),
          // scaling will occur around that point.
          transformOrigin: `${pivot.x}px ${pivot.y}px`,
          transform: `scale(${scale})`,
          // Ensure the image has its natural dimensions (or set as needed)
          width: "1920px",
          height: "1080px",
        }}
      />
    </div>
  );
};

const ImageCropper = ({ imageUrl, center, dimensions }) => {
  // Container dimensions
  const containerWidth = 400;
  const containerHeight = 400;
  
  // Compute the scale factor to fit the image inside the container
  const scale = Math.min(containerWidth / dimensions.x, containerHeight / dimensions.y);
  
  // Calculate offset so that the provided center aligns with the container's center (200,200)
  const left = (containerWidth / 2) - (center.x * scale);
  const top = (containerHeight / 2) - ((dimensions.y-center.y) * scale);

  return (
    <div
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        overflow: "hidden",
        position: "relative",
        border: "1px solid #ccc"
      }}
    >
      <img
        src={imageUrl}
        alt="Cropped"
        style={{
          position: "absolute",
          left: `${left}px`,
          top: `${top}px`,
          width: `${dimensions.x * scale}px`,
          height: `${dimensions.y * scale}px`,
        }}
      />
    </div>
  );
};

interface CanvasImageWithCenterProps {
  image: HTMLImageElement;
  // The "center" coordinate in the image's natural coordinate system (px)
  center: { x: number; y: number };
  // The natural dimensions of the image (px)
  dimensions: { x: number; y: number };
  canvasWidth?: number;
  canvasHeight?: number;
  // The frame height (in pixels) to be used as the scale basis
  frameHeight: number;
  // Rotation in radians
  rotation: number;
}

export const CanvasImageWithCenter: React.FC<CanvasImageWithCenterProps> = ({
  image,
  center,
  dimensions,
  canvasWidth = 400,
  canvasHeight = 400,
  frameHeight,
  rotation,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Clear previous canvas content
    context.clearRect(0, 0, canvasWidth, canvasHeight);
    
    if (image.complete && image.naturalWidth !== 0) {
      // Compute the scale factor based on the provided frameHeight relative to natural image height
      let scale = dimensions.y / frameHeight;
      scale *= canvasHeight / dimensions.y;

      // Determine the canvas center
      const canvasCenterX = canvasWidth / 2;
      const canvasCenterY = canvasHeight / 2;
      
      // Compute the image center point.
      // Note: here we flip the Y coordinate if needed because image coordinates origin is top-left.
      const imageCenterX = center.x;
      const imageCenterY = dimensions.y - center.y;

      // Save the context state.
      context.save();
      // Move origin to the center of the canvas.
      context.translate(canvasCenterX, canvasCenterY);
      // Rotate the canvas by the provided angle.
      context.rotate(-rotation);
      //note: my roll numbers look way off.
      console.log("rotation correction", -rotation);
      // After rotation the desired drawing offset is such that the desired image center aligns with the origin.
      // That means that we need to offset drawing the image by negative the image center (scaled).
      const offsetX = -imageCenterX * scale;
      const offsetY = -imageCenterY * scale;

      // Draw the scaled image, rotated about the canvas center.
      context.drawImage(image, offsetX, offsetY, dimensions.x * scale, dimensions.y * scale);
      
      // Restore the context to its original state.
      context.restore();
    }
  }, [image, center, dimensions, canvasWidth, canvasHeight, frameHeight, rotation]);

  return <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} style={{ border: '1px solid #ccc' }} />;
};

export default App;